import { keepPreviousData } from "@tanstack/react-query"
import { useMemo } from "react"

import { useDemand2Scope } from "~/components/demand-2/useDemand2Scope"
import { api } from "~/utils/api"
import type { FramingSignal } from "~/utils/demandFramingSignal"
import { formatNumberWithSign } from "~/utils/formatting"

const EMPTY: FramingSignal[] = []

/**
 * The Cannibalization signals as the server computed them, the same read the Framing card quotes.
 * Not stored rows: rendered read-only, and `buildSetupImpacts` names the engine's `framing:`
 * attribution keys through `byId`. Refreshed with `ENGINE_DERIVED_READS`.
 */
export function useFramingSignals(): {
  signals: FramingSignal[]
  byId: Map<string, FramingSignal>
  forVariant: (variantId: string) => FramingSignal[]
  isLoading: boolean
} {
  const { collectionIds } = useDemand2Scope()
  const q = api.demand2.getFramingSignals.useQuery(
    { collectionIds },
    { enabled: collectionIds.length > 0, placeholderData: keepPreviousData },
  )
  const signals = q.data ?? EMPTY
  const byId = useMemo(() => new Map(signals.map((s) => [s.id, s])), [signals])
  const byVariant = useMemo(() => {
    const out = new Map<string, FramingSignal[]>()
    for (const s of signals)
      for (const variantId of s.variantIds) {
        const list = out.get(variantId)
        if (list) list.push(s)
        else out.set(variantId, [s])
      }
    return out
  }, [signals])
  return {
    signals,
    byId,
    forVariant: (variantId) => byVariant.get(variantId) ?? EMPTY,
    isLoading: q.isLoading,
  }
}

/** "+8.2%" — one decimal, like the sales index it was reconciled from. */
export const framingSignalValueLabel = (s: FramingSignal): string =>
  s.pct == null ? "—" : `${formatNumberWithSign(s.pct, 1)}%`
